import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PublicChatWidget from "@/components/PublicChatWidget";
import { Search, MapPin, Star, ArrowUpRight } from "lucide-react";
import {
  T, FONT_DISPLAY, FONT_MONO, PageShell, TopNav, Footer,
  container, eyebrow, h2, italicAccent, input as inputStyle,
} from "@/lib/hybridTheme";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Directory() {
  const [rows, setRows] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [agent, setAgent] = useState(null);

  const MIN_CLIENTS_TO_SHOW = 10;
  const [dirEnabled, setDirEnabled] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: s }, cfg] = await Promise.all([
          axios.get(`${BACKEND}/api/public/directory`),
          axios.get(`${BACKEND}/api/public/site-config`),
          axios.get(`${BACKEND}/api/public/directory-config`).catch(() => ({ data: { enabled: true } })),
        ]);
        setRows(d.businesses || []);
        setIndustries(d.industries || []);
        setAgent(s.agent || null);
        setDirEnabled(cfg.data?.enabled !== false);
      } catch { /* soft-fail */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (selectedIndustry !== "all" && (r.industry || "").trim() !== selectedIndustry) return false;
      if (needle) {
        const hay = `${r.business_name || ""} ${r.industry || ""} ${r.city || ""} ${r.state || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, selectedIndustry]);

  const showComingSoon = !loading && (!dirEnabled || rows.length < MIN_CLIENTS_TO_SHOW);

  return (
    <PageShell testId="directory-page">
      <TopNav activeKey="directory" />

      {showComingSoon && (
        <section style={{ padding: "120px 0 96px" }}>
          <div style={container()}>
            <div style={{ maxWidth: 640, textAlign: "center", margin: "0 auto" }}>
              <div style={eyebrow()}>Coming soon</div>
              <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(40px, 5vw, 64px)", lineHeight: 1.05, margin: "16px 0 20px", color: T.ink, fontWeight: 400 }}>
                Directory <em style={italicAccent(T.coral)}>launching soon.</em>
              </h1>
              <p style={{ fontSize: 18, lineHeight: 1.6, color: T.inkMuted, marginBottom: 32 }}>
                We&apos;re curating a showcase of businesses we&apos;ve onboarded. Come back once we&apos;ve helped more local businesses win — or claim your spot today.
              </p>
              <Link to="/pricing-public" style={{ display: "inline-block", background: T.coral, color: T.cream, padding: "14px 28px", fontWeight: 600, textDecoration: "none", fontSize: 15 }}>
                Get your free website →
              </Link>
              <div style={{ marginTop: 20 }}>
                <Link to="/preview/hybrid" target="_blank" rel="noopener" style={{ color: T.ink, fontSize: 14, textDecoration: "underline" }}>
                  Or preview a sample site
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
      {!showComingSoon && (
      <>
      <section style={{ padding: "96px 0 40px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -80, right: -100, width: 500, height: 500, background: "radial-gradient(circle, rgba(229,83,59,0.06) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(50px)" }} />
        <div style={container()}>
          <div style={{ maxWidth: 900, position: "relative" }}>
            <div style={eyebrow()}>Directory</div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(48px, 6vw, 84px)", lineHeight: 1.02, letterSpacing: "-0.03em", fontWeight: 400, margin: "16px 0 20px", color: T.ink }}>
              Businesses we&apos;ve <em style={italicAccent(T.coral)}>onramped.</em>
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: T.inkMuted, maxWidth: 640, margin: 0 }}>
              Real local businesses that let their AI concierge handle every ring. Browse by industry or city.
            </p>
          </div>

          <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "1fr 240px", gap: 12, maxWidth: 820 }}>
            <div style={{ position: "relative" }}>
              <Search size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.inkFaint }} />
              <input
                data-testid="directory-search"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, city, industry…"
                style={{ ...inputStyle(), paddingLeft: 40 }}
              />
            </div>
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              data-testid="directory-industry-select"
              style={{ ...inputStyle(), cursor: "pointer" }}
            >
              <option value="all">All industries</option>
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section style={{ padding: "40px 0 96px" }}>
        <div style={container()}>
          <div style={{ marginBottom: 20, fontSize: 12, fontFamily: FONT_MONO, color: T.inkMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {loading ? "Loading…" : `${filtered.length} businesses`}
          </div>

          {!loading && filtered.length === 0 && (
            <div style={{ padding: "80px 20px", textAlign: "center", background: T.panel, border: `1px solid ${T.line}` }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, fontStyle: "italic", color: T.ink }}>No matches yet.</div>
              <p style={{ color: T.inkMuted, marginTop: 8, fontSize: 14 }}>Try a different industry or clear the search.</p>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            {filtered.map((r, i) => (
              <div key={r.id || i} data-testid={`directory-item-${r.id || i}`} style={{
                background: T.panel, border: `1px solid ${T.line}`, padding: "24px 22px",
                transition: "all 200ms", cursor: "default",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.coral; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 24px -8px rgba(229,83,59,0.15)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500, color: T.ink, lineHeight: 1.15 }}>{r.business_name || "—"}</div>
                <div style={{ fontSize: 12, color: T.coral, fontFamily: FONT_MONO, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 6 }}>{r.industry || "Business"}</div>
                <div style={{ marginTop: 16, fontSize: 13, color: T.inkMuted, display: "flex", alignItems: "center", gap: 6 }}>
                  <MapPin size={13} color={T.inkFaint} /> {[r.city, r.state].filter(Boolean).join(", ") || "—"}
                </div>
                {typeof r.rating === "number" && r.rating > 0 && (
                  <div style={{ marginTop: 6, fontSize: 13, color: T.inkMuted, display: "flex", alignItems: "center", gap: 6 }}>
                    <Star size={13} fill={T.brass} color={T.brass} strokeWidth={0} /> {r.rating.toFixed(1)}
                    {r.review_count > 0 && <span style={{ color: T.inkFaint }}>({r.review_count})</span>}
                  </div>
                )}
                {r.website && (
                  <a href={r.website} target="_blank" rel="noreferrer" style={{ marginTop: 16, fontSize: 12, color: T.coral, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
                    Visit site <ArrowUpRight size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "72px 0 120px" }}>
        <div style={container()}>
          <div style={{ background: T.ink, color: T.cream, padding: "48px 48px", textAlign: "center" }}>
            <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 40, fontWeight: 400, letterSpacing: "-0.02em", margin: 0 }}>
              Want your business <em style={italicAccent(T.coral)}>on this list?</em>
            </h3>
            <p style={{ marginTop: 14, opacity: 0.75, maxWidth: 560, margin: "14px auto 24px", lineHeight: 1.6 }}>
              Book a demo. We build your free preview site in 24 hours and your business is featured here for free.
            </p>
            <Link to="/#contact" style={{ background: T.coral, color: T.cream, border: "none", padding: "16px 28px", fontSize: 15, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
              Book my demo <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      </>
      )}

      <Footer />
      <PublicChatWidget agent={agent} />
    </PageShell>
  );
}
